import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from 'sonner';
import '@/App.css';

// Pages
import LandingPage from './pages/LandingPage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import DriverPage from './pages/DriverPage';
import NewOrderPage from './pages/NewOrderPage';
import OrderDetailPage from './pages/OrderDetailPage';
import CustomerVerificationPage from './pages/CustomerVerificationPage';
import DriverVerificationPage from './pages/DriverVerificationPage';
import AdminPage from './pages/AdminPage';

// Route access constants
const CUSTOMER_ONLY = ['customer'];
const DRIVER_ONLY = ['driver'];
const ADMIN_ONLY = ['admin'];

// Protected Route Component
const ProtectedRoute = ({ children, allowedTypes = ['customer', 'driver'] }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">A carregar...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!allowedTypes.includes(user?.user_type)) {
    // Redirect to appropriate dashboard based on user type
    if (user?.user_type === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user?.user_type === 'driver') {
      return <Navigate to="/driver" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

// Public Route (redirect if authenticated)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-gray-500">A carregar...</div>
      </div>
    );
  }

  if (isAuthenticated) {
    // Redirect to appropriate dashboard based on user type
    if (user?.user_type === 'admin') {
      return <Navigate to="/admin" replace />;
    } else if (user?.user_type === 'driver') {
      return <Navigate to="/driver" replace />;
    } else {
      return <Navigate to="/dashboard" replace />;
    }
  }

  return children;
};

function AppRoutes() {
  return (
    <Routes>
      {/* Public Routes */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={
        <PublicRoute>
          <LoginPage />
        </PublicRoute>
      } />
      <Route path="/register" element={
        <PublicRoute>
          <RegisterPage />
        </PublicRoute>
      } />

      {/* Customer Routes */}
      <Route path="/dashboard" element={
        <ProtectedRoute allowedTypes={CUSTOMER_ONLY}>
          <DashboardPage />
        </ProtectedRoute>
      } />
      <Route path="/new-order" element={
        <ProtectedRoute allowedTypes={CUSTOMER_ONLY}>
          <NewOrderPage />
        </ProtectedRoute>
      } />
      <Route path="/verification/customer" element={
        <ProtectedRoute allowedTypes={CUSTOMER_ONLY}>
          <CustomerVerificationPage />
        </ProtectedRoute>
      } />

      {/* Driver Routes */}
      <Route path="/driver" element={
        <ProtectedRoute allowedTypes={DRIVER_ONLY}>
          <DriverPage />
        </ProtectedRoute>
      } />
      <Route path="/verification/driver" element={
        <ProtectedRoute allowedTypes={DRIVER_ONLY}>
          <DriverVerificationPage />
        </ProtectedRoute>
      } />

      {/* Admin Routes */}
      <Route path="/admin" element={
        <ProtectedRoute allowedTypes={ADMIN_ONLY}>
          <AdminPage />
        </ProtectedRoute>
      } />

      {/* Shared Routes */}
      <Route path="/orders/:orderId" element={
        <ProtectedRoute>
          <OrderDetailPage />
        </ProtectedRoute>
      } />

      {/* Fallback */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" richColors />
        </div>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
